import { useCallback, useEffect, useRef, useState } from "react";

import api from "../api/axios";

const UNAVAILABLE_STATUS = {
  enabled: true,
  title: "We are temporarily unavailable",
  message:
    "We cannot safely connect to our services right now. Please wait a moment and try again. Your saved cart and account details are safe.",
  estimatedEndAt: null,
  updatedAt: null,
  unavailable: true,
};

const normaliseStatus = (value = {}) => ({
  enabled: Boolean(value.enabled),
  title: value.title || "We will be back shortly",
  message:
    value.message ||
    "We are making a few thoughtful improvements. Your saved cart and account details are safe.",
  estimatedEndAt: value.estimatedEndAt || null,
  updatedAt: value.updatedAt || null,
  unavailable: false,
});

const useMaintenanceMode = () => {
  const mountedRef = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [maintenance, setMaintenance] = useState({
    ...UNAVAILABLE_STATUS,
    enabled: false,
    unavailable: false,
  });

  const refresh = useCallback(async () => {
    if (mountedRef.current) {
      setChecking(true);
    }

    try {
      const response = await api.get("/maintenance/status", {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (mountedRef.current) {
        setMaintenance(normaliseStatus(response.data?.data));
      }
    } catch {
      if (mountedRef.current) {
        // Fail closed when the backend cannot be reached. This prevents a
        // customer from entering checkout while services are being deployed.
        setMaintenance(UNAVAILABLE_STATUS);
      }
    } finally {
      if (mountedRef.current) {
        setChecking(false);
        setInitialLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();

    const timer = window.setInterval(refresh, 30000);

    const checkWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };

    const handleMaintenanceResponse = (event) => {
      setMaintenance(normaliseStatus(event.detail));
      setInitialLoading(false);
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", checkWhenVisible);
    window.addEventListener(
      "storefront-maintenance",
      handleMaintenanceResponse
    );

    return () => {
      mountedRef.current = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", checkWhenVisible);
      window.removeEventListener(
        "storefront-maintenance",
        handleMaintenanceResponse
      );
    };
  }, [refresh]);

  return {
    maintenance,
    initialLoading,
    checking,
    refresh,
  };
};

export default useMaintenanceMode;
